/**
 * share.js
 *
 * One way to hand text to the system share sheet, so call sites do not each
 * work out which API the platform has.
 *
 * Native goes through @capacitor/share: Android's WebView has no Web Share
 * API at all. A browser uses navigator.share where it exists and the
 * clipboard where it does not (desktop Firefox, some desktop Chrome), and
 * says which happened so the caller can tell the user their text was copied
 * rather than appear to do nothing.
 *
 * Resolves to "shared", "copied", "cancelled" or "failed". Never throws:
 * dismissing the sheet rejects on every platform, and that is not an error.
 */
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';

const isCancel = (err) =>
  err?.name === 'AbortError' || /cancel/i.test(err?.message || '');

export async function shareText(text, { title } = {}) {
  if (Capacitor.isNativePlatform()) {
    try {
      await Share.share({ title, text, dialogTitle: title });
      return 'shared';
    } catch (err) {
      return isCancel(err) ? 'cancelled' : 'failed';
    }
  }

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text });
      return 'shared';
    } catch (err) {
      if (isCancel(err)) return 'cancelled';
      // Anything else (a browser that exposes share but refuses this
      // payload) falls through to the clipboard.
    }
  }

  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    return legacyCopy(text) ? 'copied' : 'failed';
  }
}

// navigator.clipboard and navigator.share exist only on a secure page, so a
// phone opening the dev server over http://192.168... had neither and every
// share failed. The old select-and-copy route still works there.
function legacyCopy(text) {
  if (typeof document === 'undefined') return false;
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  area.setSelectionRange(0, text.length);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  area.remove();
  return ok;
}
